import * as Yup from 'yup';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { DistributionCenter } from '@api/DistributionCenters/DistributionCenter';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { ProfileTypes, UserType } from '@api/Users/User';
import UserService from '@api/Users/UserCtrl';

interface ModalSaveDistributionCenterProps {
    modalAddDistributionCenter: OpenModalHookType;
    distributionCenter?: DistributionCenter;
    onAddDistributionCenter: (distributionCenter: DistributionCenter) => void;
}

const saveDistributionCenterSchema = Yup.object({
    uid: Yup.string(),
    name: Yup.string().required('Nombre es requerido'),
    phone: Yup.string().required('Número de teléfono es requerido'),
    email: Yup.string().required('Correo es requerido'),
    managerId: Yup.string().required('Encargado es requerido'),
    address: Yup.string().required('Dirección es requerida')
});

export const ModalSaveDistributionCenter: React.FunctionComponent<ModalSaveDistributionCenterProps> = (props: any) => {
    const { modalAddDistributionCenter, distributionCenter, onAddDistributionCenter } = props;
    const userService = useMemo(() => new UserService(), []);
    const [users, setUsers] = useState<UserType[]>([]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm<DistributionCenter>({
        resolver: yupResolver(saveDistributionCenterSchema)
    });

    const getUsers = async () => {
        showLoader();
        try {
            const data = await userService.getUsersByProfile(ProfileTypes.DistributionCenter);
            setUsers(data);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const handleClose = () => {
        modalAddDistributionCenter.toggle();
    };

    const onSaveDistributionCenter: SubmitHandler<DistributionCenter> = async (values) => {
        onAddDistributionCenter(values);
    };

    useEffect(() => {
        void getUsers();
        if (modalAddDistributionCenter.modalOpen) {
            reset({
                id: distributionCenter?.id || undefined,
                name: distributionCenter?.name || '',
                phone: distributionCenter?.phone || '',
                email: distributionCenter?.email || '',
                managerId: distributionCenter?.managerId || '',
                address: distributionCenter?.address || ''
            });
        }
    }, [modalAddDistributionCenter.modalOpen]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalAddDistributionCenter.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-save-distributionCenter-title"
            aria-describedby="modal-save-distributionCenter-description"
            fullWidth
            maxWidth="md"
        >
            <DialogTitle id="modal-save-distributionCenter-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        {distributionCenter?.id ? 'Editar Cedis' : 'Nuevo Cedis'}
                    </Typography>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <form id="add-distributionCenter-form" onSubmit={handleSubmit(onSaveDistributionCenter)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">
                                Antes de completar los datos de un CEDIS, agrega al usuario que será encargado del
                                mismo.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        autoFocus
                                        label="Nombre"
                                        variant="outlined"
                                        autoComplete="firstname"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.name}
                                        helperText={errors.name && errors.name.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="phone"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        autoFocus
                                        label="Número de teléfono"
                                        variant="outlined"
                                        autoComplete="phone"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.phone}
                                        helperText={errors.phone && errors.phone.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        autoFocus
                                        label="Correo"
                                        variant="outlined"
                                        autoComplete="email"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.email}
                                        helperText={errors.email && errors.email.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel id="manager-select-label">Encargado</InputLabel>
                                <Controller
                                    name="managerId"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Select
                                            labelId="manager-select-label"
                                            id="manager-select"
                                            value={value}
                                            label="Encargado"
                                            onChange={onChange}
                                            error={!!errors.managerId}
                                        >
                                            {users.map((user, index) => (
                                                <MenuItem key={index} value={user.uid}>
                                                    {user.profile.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Controller
                                name="address"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        autoFocus
                                        label="Dirección"
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.address}
                                        helperText={errors.address && errors.address.message}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Box flex={1} textAlign="center">
                    <Button
                        sx={{ width: 300 }}
                        color="primary"
                        variant="contained"
                        type="submit"
                        form="add-distributionCenter-form"
                    >
                        Guardar
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
